import { useForm } from 'react-hook-form'
import React, { useCallback } from 'react'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Button,
  Indicators,
} from 'ui'
import toast from 'react-hot-toast'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { updateSession } from '../../../session'
import { getCookie } from 'react-simple-cookie-store'
import { AccessToken } from '../../../session/AccessToken'

const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  passwordConfirm: Yup.string()
    .required('Password confirmation is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
})

export type ResetPasswordForm = Yup.InferType<typeof resetPasswordSchema>

/**
 * A password reset form for users who clicked the reset link in their email.
 * Uses the temporary token stored in cookies to authenticate the reset request.
 */
export const ResetPassword: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Get temporary token from cookie storage (not Redux)
  const temporaryAccessToken = getCookie(AccessToken.Temporary) || ''

  const form = useForm<ResetPasswordForm>({
    mode: 'onBlur',
    resolver: yupResolver(resetPasswordSchema as any) as any,
    defaultValues: {
      password: '',
      passwordConfirm: '',
    },
  })
  const handleFormSubmission = useCallback(
    async (data: ResetPasswordForm) => {
      if (!temporaryAccessToken) {
        toast.error('Reset link is invalid or has expired')
        navigate('/login/forgot')
        return
      }

      try {
        const apiUrl =
          import.meta.env.VITE_EPC_API_URL ??
          import.meta.env.REACT_APP_API_URL ??
          ''

        const response = await fetch(`${apiUrl}/auth/reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${temporaryAccessToken}`,
          },
          body: JSON.stringify({
            password: data.password,
            passwordConfirm: data.passwordConfirm,
          }),
        })

        const result = await response.json()

        if (!response.ok) {
          // Handle specific error cases
          if (response.status === 401) {
            toast.error(result.detail || 'Reset link has expired')
            navigate('/login/forgot')
          } else if (response.status === 400) {
            toast.error(result.detail || 'Invalid password')
          } else {
            toast.error('Failed to reset password')
          }
          return
        }

        // Success! Update session with new tokens
        dispatch(
          updateSession({
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
            temporaryAccessToken: '', // Clear temp token
          })
        )

        toast.success('Password reset successfully!')

        // Redirect to dashboard
        navigate('/app/dashboard')
      } catch (error: any) {
        console.error('Password reset error:', error)
        toast.error(error.message || 'Could not reach server')
      }
    },
    [dispatch, navigate, temporaryAccessToken]
  )

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmission)}
        className="mt-8 flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>New Password</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder="Enter your new password"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="passwordConfirm"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Confirm New Password</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  placeholder="Confirm your new password"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={form.formState.isSubmitting}
          variant="default"
          className="ml-auto mt-4"
        >
          {form.formState.isSubmitting ? (
            <Indicators.Spinner size="lg" className="mt-auto mr-2 mb-auto" />
          ) : null}
          <span>Reset Password</span>
        </Button>
      </form>
    </Form>
  )
}
